import { useMutation, useQueryClient } from "@tanstack/react-query";
import { message } from "antd";
import createPrivateAdminPanelInstance from "../../api/PrivateAdminPanelInstance";
import { API_ROUTES } from "../../api/ApiRoutes";
import { AppStrings } from "../../utils/constants/AppStrings";
import { getApiErrorMessage } from "../../utils/ErrorUtils";
import { bonusAdminEnvelope } from "./bonusAdminBody";
import { getTemplateQueryKey } from "./useGetTemplate";
import { LIST_TEMPLATES_QUERY_KEY } from "./useListTemplates";
import type { TemplateIn, TemplateOut } from "../../components/Promotions/DynamicBonuses/types";

type StoredToken = { AccessToken?: string };

type UpdateTemplateVars = { templateId: string; payload: TemplateIn };

type UpdateTemplateResult = { body: TemplateOut; message?: string };

async function putTemplate({ templateId, payload }: UpdateTemplateVars): Promise<UpdateTemplateResult> {
  const tokenString = localStorage.getItem("token");
  const token: StoredToken | null = tokenString ? JSON.parse(tokenString) : null;
  const instance = createPrivateAdminPanelInstance(token?.AccessToken);
  const url = API_ROUTES.UPDATE_DYNAMIC_BONUS_TEMPLATE.replace("{template_id}", templateId);
  const response = await instance.put(url, payload);
  const envelope = bonusAdminEnvelope<TemplateOut>(response);
  if (!envelope.body) {
    throw new Error("Template update returned no data");
  }
  return { body: envelope.body, message: envelope.message };
}

export const useUpdateTemplate = (templateId: string) => {
  const queryClient = useQueryClient();

  return useMutation<UpdateTemplateResult, Error, TemplateIn>({
    mutationFn: (payload) => putTemplate({ templateId, payload }),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: LIST_TEMPLATES_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: getTemplateQueryKey(templateId) });
      message.success(result.message || AppStrings.PAM_GENERIC_REQUEST_SUCCEEDED);
    },
    onError: (error) => {
      message.error(getApiErrorMessage(error, AppStrings.PAM_GENERIC_REQUEST_FAILED));
    },
  });
};
